import type { NiyaazPayableEvent } from "@/utils/app-formatters";
import type { EventTypeChoice } from "@/constants";

export type EventSequenceRow = {
  id?: string;
  type: "NIYAAZ_FORM" | "NIYAAZ_RECEIPT";
  venueCode?: string | null;
  prefix: string;
  currentValue?: number;
};

export type EventRelayEventSummary = {
  id?: string;
  name?: string;
};

/** Event row from `events` list + dashboard cards */
export type EventsListRecord = NiyaazPayableEvent & {
  id: string | number;
  name: string;
  type?: EventTypeChoice;
  hijriYear?: string | number;
  slug?: string;
  fromDate?: string;
  toDate?: string;
  image?: string | null;
  relayEventId?: string | null;
  relayEvent?: EventRelayEventSummary | null;
  venues?: EventVenueRow[];
  sequences?: EventSequenceRow[];
};

/** Stored in react-admin `currentEvent` store (localStorage subset + full record) */
export type EventVenueRow = {
  code: string;
  label: string;
  role: "JAMAN" | "NAMAAZ";
  sortOrder?: number;
};

export type CurrentEvent = NiyaazPayableEvent & {
  id?: string | number;
  name?: string;
  type?: EventTypeChoice;
  slug?: string;
  hijriYear?: string | number;
  image?: string | null;
  fromDate?: string;
  toDate?: string;
  relayEventId?: string | null;
  relayEvent?: EventRelayEventSummary | null;
  venues?: EventVenueRow[];
  sequences?: EventSequenceRow[];
};

export type EventStatsAttendanceSlice = {
  gentsCount: number;
  ladiesCount: number;
  childrenUnder10: number;
  totalCount: number;
};

export type EventStatsNiyaazRow = {
  markaz: string;
  name: string;
  count: number;
  gentsCount: number;
  ladiesCount: number;
  childrenUnder10: number;
  totalCount: number;
  physical: EventStatsAttendanceSlice;
  relay: EventStatsAttendanceSlice;
  takhmeenAmount: number;
  chairs: number;
  gentsChairs: number;
  ladiesChairs: number;
  iftaari: number;
  zabihat: number;
  /** Forms with at least one relay (remote) member */
  relayRegistrations: number;
  /** Forms with relayRequired flag (legacy / form-level) */
  relayRequiredForms?: number;
  pirsa: number;
  paidAmount: number;
  totalPayable: number;
};

export type EventStatsNamaazRow = {
  namaazVenue: string;
  name: string;
  count: number;
  gentsCount: number;
  ladiesCount: number;
  childrenUnder10: number;
  totalCount: number;
};

/** Matches `ReceiptGroupRow` shape used by `receiptGroupBy` */
export type DayWiseReceiptReportRow = {
  markaz: string;
  name?: string;
  day: string;
  mode: string;
  totalAmount: number;
};

/** Per markaz: day → payment mode → amount (output shape of `receiptGroupBy`) */
export type ReceiptGroupedMap = Record<string, Record<string, Record<string, number>>>;

export type EventStatsApiPayload = {
  niyaazCounts: EventStatsNiyaazRow[];
  namaazVenueCounts: EventStatsNamaazRow[];
  dayWiseReceiptReport: DayWiseReceiptReportRow[];
};

export function isEventStatsPayload(data: unknown): data is EventStatsApiPayload {
  if (data == null || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    Array.isArray(d.niyaazCounts) &&
    Array.isArray(d.namaazVenueCounts) &&
    Array.isArray(d.dayWiseReceiptReport)
  );
}

export type EventRelayAccessSyncPreviewStatus =
  | "create"
  | "exists"
  | "remove"
  | "error"
  | "update_reason";

export type EventRelayAccessSyncPreviewRow = {
  id: string;
  itsId: string;
  memberName: string | null;
  memberSource: string | null;
  niyaazId: string | null;
  formNo: string | number | null;
  hofName: string | null;
  markaz: string | null;
  relayReason: string | null;
  itsSource?: "directory" | "sabil_only" | "nowhere";
  status: EventRelayAccessSyncPreviewStatus;
  statusLabel: string;
  errorReason: string | null;
  relayAccessRequestId: string | null;
  selectable: boolean;
  defaultSelected: boolean;
  syncAction: "create" | "revoke" | "update_reason" | null;
};

export type EventRelayAccessSyncPreviewResult = {
  eventId: string;
  relayEventId: string | null;
  relayEvent: EventRelayEventSummary | null;
  rows: EventRelayAccessSyncPreviewRow[];
  summary: {
    create: number;
    exists: number;
    remove: number;
    error: number;
  };
};

export type EventRelayAccessSyncResultRow = {
  itsId: string;
  action: string;
  ok: boolean;
  error?: string;
};

export type EventRelayAccessSyncResponse = {
  results: EventRelayAccessSyncResultRow[];
  preview: EventRelayAccessSyncPreviewResult;
};

export function isEventRelayAccessSyncPreviewResult(
  data: unknown
): data is EventRelayAccessSyncPreviewResult {
  if (data == null || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.rows) && d.summary != null && typeof d.summary === "object";
}
